import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Typography } from "@material-ui/core";
import JobManager from "../components/JobManager";
import ProcessamentoInputCell from "../components/ProcessamentoInputCell";
import ExtractionModeChip from "../components/ExtractionModeChip";
import { getJobExtractionMode } from "../constants/extractionModes";
import {
  Section,
  MetaGrid,
  MetaRow,
  JobMetaSection,
} from "../components/JobDetailsDrawer";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";
import {
  Archive,
  Unarchive,
  Sync,
  FileCopy,
  DeleteOutline,
  GetApp,
  Settings,
  Speed,
  PlaylistAddCheck,
  CheckCircleOutline,
} from "@material-ui/icons";
import { getApiBaseUrl } from "../config";
import { jobAllowsManualReprocess } from "../utils/jobReprocess";
import { persistProcessamentoJobForReplication } from "../utils/replicateProcessamentoJob";
import SoftDeleteJobDialog from "../components/SoftDeleteJobDialog";

const API_BASE_URL = getApiBaseUrl();

const SOFT_DELETE_ALLOWED_STATUSES = ["DONE", "FINAL_ERROR", "ERROR_FINALIZING"];

const ProcessamentosPage = () => {
  const history = useHistory();
  const { t } = useTranslation("jobs");
  const { hasRole, token } = useAuth();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [softDeleteTarget, setSoftDeleteTarget] = useState(null);

  /* Tabela enxuta — só o essencial para identificar o job na lista. Detalhes
     completos (célula, template, documento de entrada, configurações,
     output completo) ficam no drawer lateral. */
  const columns = [
    {
      key: "id",
      label: t("processings.columns.id"),
      type: "id",
      sortable: false,
    },
    {
      key: "insertion_timestamp",
      label: t("table.dateTime"),
      type: "date",
      sortable: true,
    },
    {
      key: "job_name",
      label: t("table.taskName"),
      type: "text",
      sortable: true,
    },
    {
      key: "status",
      label: t("table.status"),
      type: "status",
      sortable: true,
      align: "center",
      formatValue: (value) => {
        return t(`status.${value}`) || value;
      },
    },
    {
      /* sortable: false é obrigatório — sort_by vai direto para a API e
         extraction_mode vive dentro do JSON de input_params, não é coluna
         do banco; ordenar por ele cairia em insertion_timestamp em silêncio. */
      key: "extraction_mode",
      label: t("processings.columns.mode"),
      type: "custom",
      sortable: false,
      align: "center",
      renderCell: (job) => (
        <ExtractionModeChip mode={getJobExtractionMode(job)} />
      ),
    },
    {
      key: "user_name",
      label: t("table.user"),
      type: "user",
      sortable: true,
    },
    {
      key: "output_payload",
      label: t("table.outputs"),
      type: "outputs",
      sortable: false,
      align: "center",
    },
    {
      key: "actions",
      label: t("table.actions"),
      type: "actions",
      sortable: false,
    },
  ];

  // Configuração dos campos de filtro
  const filterFields = [
    // Filtros obrigatórios para todos os tipos de trabalho
    {
      key: "job_id",
      label: t("filters.jobId"),
      type: "text",
      placeholder: t("filters.jobIdPlaceholder"),
    },
    {
      key: "job_name_filter",
      label: t("filters.taskName"),
      type: "text",
      placeholder: t("filters.taskNamePlaceholder"),
    },
    {
      key: "external_id",
      label: t("filters.externalId"),
      type: "text",
      placeholder: t("filters.externalIdPlaceholder"),
    },
    {
      key: "date_start",
      label: t("filters.dateStart"),
      type: "date",
    },
    {
      key: "date_end",
      label: t("filters.dateEnd"),
      type: "date",
    },
    // Filtros específicos para processamentos
    {
      key: "user_name_filter",
      label: t("filters.user"),
      type: "text",
      placeholder: t("filters.userPlaceholder"),
    },
    {
      key: "status_filter",
      label: t("filters.status"),
      type: "select",
      options: [
        { value: "WAITING", label: t("status.WAITING") },
        { value: "RUNNING", label: t("status.RUNNING") },
        { value: "DONE", label: t("status.DONE") },
        { value: "ERROR", label: t("status.ERROR") },
        { value: "FINAL_ERROR", label: t("status.FINAL_ERROR") },
        { value: "DISCARDED", label: t("status.DISCARDED") },
      ],
    },
    {
      key: "task_type_filter",
      label: t("filters.taskType"),
      type: "select",
      options: [
        {
          value: "PDF_JSON_EXTRACTION",
          label: t("jobStatusViewer.taskTypes.PDF_JSON_EXTRACTION"),
        },
      ],
    },
    {
      key: "output_payload_text_filter",
      label: t("filters.outputContent"),
      type: "text",
      placeholder: t("filters.outputContentPlaceholder"),
    },
    {
      key: "include_archived",
      label: t("filters.showArchived"),
      type: "select",
      options: [
        { value: "", label: t("filters.showArchivedNo") },
        { value: "true", label: t("filters.showArchivedYes") },
      ],
    },
  ];

  const apiHeaders = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ServiceName: "PROCESS_DIGITAL_PDF",
  };

  const handleArchiveJob = async (job) => {
    try {
      const response = await fetch(`${API_BASE_URL}/job/${job.id}/archive`, {
        method: "PATCH",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao arquivar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao arquivar tarefa:", error);
    }
  };

  const handleUnarchiveJob = async (job) => {
    try {
      const response = await fetch(`${API_BASE_URL}/job/${job.id}/unarchive`, {
        method: "PATCH",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao desarquivar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao desarquivar tarefa:", error);
    }
  };

  const handleSoftDeleteJob = async (job) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/job/${job.id}/soft_delete`,
        {
          method: "PATCH",
          headers: apiHeaders,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao ocultar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao ocultar tarefa:", error);
    }
  };

  const handleReprocessJob = async (job) => {
    try {
      const response = await fetch(`${API_BASE_URL}/job/${job.id}/reprocess`, {
        method: "PATCH",
        headers: apiHeaders,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao reprocessar tarefa");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao reprocessar tarefa:", error);
    }
  };

  const handleBatchArchive = async (jobIds) => {
    if (!jobIds.length) return;
    try {
      const response = await fetch(`${API_BASE_URL}/job/batch_archive`, {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({ job_ids: jobIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao arquivar em lote");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao arquivar em lote:", error);
    }
  };

  const handleBatchUnarchive = async (jobIds) => {
    if (!jobIds.length) return;
    try {
      const response = await fetch(`${API_BASE_URL}/job/batch_unarchive`, {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({ job_ids: jobIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao desarquivar em lote");
      }
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Erro ao desarquivar em lote:", error);
    }
  };

  // Reprocessar: mesma regra do PATCH /job/:id/reprocess (ADMIN + re_execute_job)
  const canReprocess = hasRole("ADMIN", "re_execute_job");
  const canArchive = hasRole("PROCESS_DIGITAL_PDF", "archive_job");
  const canDelete = hasRole("PROCESS_DIGITAL_PDF", "delete_job");
  const canReplicate = hasRole("PROCESS_DIGITAL_PDF", "exec_process");

  const handleReplicateJob = (job) => {
    if (!persistProcessamentoJobForReplication(job)) {
      return;
    }
    history.push("/processamento/novo");
  };

  /* Ver `ContestacaoPage` para a semântica dos tiers. */
  const customActions = [
    {
      key: "reprocess",
      tier: "primary",
      label: t("actions.primary.retry"),
      icon: <Sync />,
      tooltip: t("actions.reprocessTooltip"),
      hidden: (job) =>
        !canReprocess || !jobAllowsManualReprocess(job),
      onClick: (job) => handleReprocessJob(job),
    },
    {
      key: "replicate",
      tier: "menu",
      icon: <FileCopy />,
      label: t("actions.replicate"),
      tooltip: t("actions.replicateTooltip"),
      hidden: (job) =>
        !canReplicate ||
        !job?.input_payload ||
        !job?.input_params?.document?.token,
      onClick: (job) => handleReplicateJob(job),
    },
    {
      key: "archive",
      tier: "menu",
      icon: <Archive />,
      label: t("actions.archive"),
      tooltip: t("actions.archiveTooltip"),
      hidden: (job) =>
        !canArchive ||
        !!job.archived_timestamp ||
        !["DONE", "FINAL_ERROR", "ERROR_FINALIZING"].includes(job.status),
      onClick: (job) => handleArchiveJob(job),
    },
    {
      key: "unarchive",
      tier: "menu",
      icon: <Unarchive />,
      label: t("actions.unarchive"),
      tooltip: t("actions.unarchiveTooltip"),
      hidden: (job) => !canArchive || !job.archived_timestamp,
      onClick: (job) => handleUnarchiveJob(job),
    },
    {
      key: "soft_delete",
      tier: "danger",
      icon: <DeleteOutline />,
      label: t("actions.softDelete"),
      tooltip: t("actions.softDeleteTooltip"),
      hidden: (job) =>
        !canDelete ||
        !SOFT_DELETE_ALLOWED_STATUSES.includes(job.status),
      onClick: (job) => setSoftDeleteTarget(job),
    },
  ];

  // Handlers para ações
  const handleNewJob = () => {
    history.push("/processamento/novo");
  };

  const handleJobClick = (job) => {
    console.log("Tarefa clicada:", job);
    // Navegar para detalhes ou abrir modal
  };

  const handleJobAction = (action, job) => {
    console.log("Ação executada:", action, "na tarefa:", job);

    switch (action) {
      case "view":
        // Abrir modal de detalhes ou navegar para página de detalhes
        console.log("Visualizar tarefa:", job.id);
        break;

      case "download":
        // Fazer download dos resultados
        console.log("Download resultados:", job.id);
        downloadJobResults(job);
        break;

      case "reprocess":
        handleReprocessJob(job);
        break;

      case "archive":
        handleArchiveJob(job);
        break;

      case "unarchive":
        handleUnarchiveJob(job);
        break;

      case "soft_delete":
        setSoftDeleteTarget(job);
        break;

      case "replicate":
        handleReplicateJob(job);
        break;

      default:
        console.log("Ação desconhecida:", action);
    }
  };

  const downloadJobResults = (job) => {
    // Implementar download dos resultados
    if (job.output_payload) {
      try {
        const blob = new Blob([JSON.stringify(job.output_payload, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `job_${job.id}_results.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erro ao fazer download:", error);
      }
    }
  };

  /* Conteúdo do drawer lateral de detalhes de um processamento. Reúne em um
     painel rolável: informações gerais (via `JobMetaSection`), parâmetros
     da execução (máx páginas / páginas processadas) e os blocos de
     entrada/saída completos com botões de ação. */
  const renderProcessamentoDetails = (job) => {
    const inputParams = job?.input_params;
    const outputParams = job?.output_params;
    const numMaxPaginas = inputParams?.num_max_pagina_a_processar;
    const pagesProcessed = outputParams?.pages;
    const extractionMode = getJobExtractionMode(job);
    const isDone = job?.status?.toUpperCase() === "DONE";
    const hasOutput = Boolean(job?.output_payload);
    const hasResultados = Boolean(job?.resultados);
    return (
      <>
        <JobMetaSection job={job} />

        <Section
          title={t("processings.drawer.sections.execution")}
          accent="neutral"
        >
          <MetaGrid>
            <MetaRow
              icon={<Settings />}
              label={t("processings.drawer.fields.maxPages")}
              value={
                numMaxPaginas != null && numMaxPaginas !== ""
                  ? String(numMaxPaginas)
                  : null
              }
              copyValue={null}
              emptyText={t("processings.drawer.fields.maxPagesEmpty")}
            />
            <MetaRow
              icon={<Speed />}
              label={t("processings.drawer.fields.extractionMode")}
              value={<ExtractionModeChip mode={extractionMode} />}
              copyValue={null}
            />
            <MetaRow
              icon={<PlaylistAddCheck />}
              label={t("processings.drawer.fields.pagesProcessed")}
              value={
                isDone && pagesProcessed != null
                  ? String(pagesProcessed)
                  : null
              }
              copyValue={null}
              emptyText={t("processings.drawer.fields.pagesProcessedEmpty")}
            />
          </MetaGrid>
        </Section>

        <Section
          title={t("processings.drawer.sections.input")}
          accent="primary"
        >
          <ProcessamentoInputCell job={job} showExtractionMode={false} />
        </Section>

        <Section
          title={t("processings.drawer.sections.output")}
          accent="secondary"
        >
          {hasOutput || hasResultados ? (
            <Box className="flex-col gap-8">
              {hasResultados && (
                <Typography
                  variant="body2"
                  style={{
                    color: "var(--ikaro-status-success-text)",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <CheckCircleOutline style={{ fontSize: "1rem" }} />
                  {job.resultados}
                </Typography>
              )}
              {hasOutput && (
                <>
                  <Typography
                    variant="body2"
                    style={{ color: "var(--ikaro-text-secondary)" }}
                  >
                    {t("processings.drawer.empty.outputAvailable")}
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<GetApp />}
                      onClick={() => downloadJobResults(job)}
                      style={{
                        textTransform: "none",
                        borderColor: "var(--ikaro-orange-primary)",
                        color: "var(--ikaro-orange-primary)",
                      }}
                    >
                      {t("processings.drawer.actions.downloadJson")}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Typography
              variant="body2"
              style={{
                color: "var(--ikaro-text-muted)",
                fontStyle: "italic",
              }}
            >
              {t("processings.drawer.empty.noOutput")}
            </Typography>
          )}
        </Section>
      </>
    );
  };

  return (
    <>
      <JobManager
        jobType="processamento"
        title={t("processings:title")}
        subtitle={t("processings:subtitle")}
        apiEndpoint="/job/get_jobs_list"
        createJobPath="/processamento/novo"
        columns={columns}
        filterFields={filterFields}
        customActions={customActions}
        onNewJob={handleNewJob}
        onJobClick={handleJobClick}
        onJobAction={handleJobAction}
        hideViewAction={true}
        customFilters={{
          code: "PROCESS_DIGITAL_PDF", // Código específico para processamento de documentos PDF
        }}
        code="PROCESS_DIGITAL_PDF"
        showNewJobButton={hasRole("PROCESS_DIGITAL_PDF", "exec_process")}
        refreshTrigger={refreshTrigger}
        enableSelection={true}
        onBatchArchive={handleBatchArchive}
        onBatchUnarchive={handleBatchUnarchive}
        renderJobDetails={renderProcessamentoDetails}
        drawerTitle={t("processings.drawer.title")}
      />
      <SoftDeleteJobDialog
        open={softDeleteTarget != null}
        job={softDeleteTarget}
        onClose={() => setSoftDeleteTarget(null)}
        onConfirm={handleSoftDeleteJob}
      />
    </>
  );
};

export default ProcessamentosPage;
